/**
 * **重复明细**（"哪几行、哪个字/词、会怎么处理"）—— 预览卡片里那块。
 *
 * ## 为什么必须有它（用户原话）
 *
 * > "对于录表时的文本框中，如果有重复的字或词，但有提示，**几行，什么字或词**。
 * >  现在只提示重复，没有提示哪行，哪个字和词。"
 *
 * 用户真实的经历：11 行里「上」出现了两次，预览只写「同一份里重复 1 条（已合并）」。
 * 他不知道是哪个字、在哪两行 —— 于是"数字对不上"就变成"这个功能不可信"。
 * 现在每一条都说三件事：**哪个字/词**、**哪几行（几次）**、**会怎么处理**。
 *
 * ## 三个来源，一个口径
 *
 *   · 「同一份里重复·打错了」= 后端 `duplicates.merged`（服务层去重时顺路登记，走 shared 的
 *     `groupImportDuplicates()` 纯函数，`action === 'merge'`）；
 *   · 「多音字重复」= 后端 `duplicates.polyphonic`（同一个数组里 `action === 'polyphonic'`：
 *     这个字有 2+ 个读音，字表里仍然只存 1 条，**不算重复** —— 用户 2026-10-17 的要求）；
 *     ⚠️ 2026-10-21 用户要求把这一类的**说明块整块删掉**（"说明文字块太多"）：
 *     数据照旧解析（蓝底汇总块的「多音字（重复出现的）：N 个」仍用它），但这里不画；
 *   · 「之前已经录过」= 后端 `duplicates.existing`（服务层逐条判定的 `skipped`/`updated`，
 *     走 `groupExistingDuplicates()`）。
 *
 * ⛔ 这里**不解析文本、不数行号**：界面另算一遍迟早与后端漂移，而这块信息的意义
 *    正是"说得清、对得上"。本组件只做排版 + 折叠。
 *
 * ## 为什么用"提示"色而不是错误色
 *
 * 重复只会**合并 / 跳过**，行照常导入，它**不拦任何一批**。用错误色会让人以为
 * "这批有问题、要重录"。所以：
 *   · 「会合并」用琥珀色（要人看一眼，但不是错）；
 *   · 「之前已经录过」用中性灰（重复导入同一份表时这是**常态**）。
 *
 * ## 手机优先
 *
 *   · 列表 `max-h` + `overflow-y-auto`：几百条也不会把页面撑爆；
 *   · 超过 `IMPORT_DUPLICATE_FOLD_THRESHOLD` 条**先折起来**，并**始终说出总条数**
 *     （只说前 6 条又不给总数，等于又一次"只提示重复、不说清"）；
 *   · 行号是**单独的高亮小标签**（用户点名要的就是它，不能混在一段灰字里）。
 */
import { useState } from 'react';
import { describeDuplicateAction, foldDuplicateItems } from '@fht/shared';
import type { ImportDuplicateItem } from '@fht/shared';
import { cn } from '../../lib/utils';

/** 一类重复的列表（「同一份里重复」/「多音字」/「之前已经录过」各一个）。 */
export function DuplicateDetailList({
  title,
  hint,
  items,
  tone,
}: {
  title: string;
  /** 这一类"会怎么处理"的一句话说明（列表上方那一行小字）。 */
  hint: string;
  items: readonly ImportDuplicateItem[];
  tone: 'warn' | 'neutral' | 'info';
}) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;

  const folded = foldDuplicateItems(items);
  /**
   * ⚠️ 折叠是**真的不渲染**（`slice`），不是用 CSS 藏起来 ——
   *    一次重复导入整本书时"已录过"会有几百条，靠 CSS 藏着照样卡手机。
   */
  const shown = expanded ? items : folded.shown;
  const toneBox =
    tone === 'warn'
      ? 'border-amber-200 bg-amber-50'
      : tone === 'info'
        ? 'border-sky-200 bg-sky-50'
        : 'border-slate-200 bg-slate-50';
  const toneText = tone === 'warn' ? 'text-amber-900' : tone === 'info' ? 'text-sky-900' : 'text-slate-800';
  const toneHint = tone === 'warn' ? 'text-amber-800' : tone === 'info' ? 'text-sky-800' : 'text-slate-500';
  const toneBadge =
    tone === 'warn'
      ? 'bg-amber-200/80 text-amber-900'
      : tone === 'info'
        ? 'bg-sky-200/80 text-sky-900'
        : 'bg-slate-200 text-slate-700';

  return (
    <div className={cn('min-w-0 rounded-xl border px-3 py-2', toneBox)}>
      <p className={cn('flex flex-wrap items-baseline gap-2 text-sm font-semibold', toneText)}>
        <span>{title}</span>
        <span
          className={cn('rounded-full px-2 py-0.5 text-xs font-bold tabular-nums', toneBadge)}
        >
          共 {folded.total} 条
        </span>
      </p>
      <p className={cn('mt-0.5 text-xs', toneHint)}>{hint}</p>

      <ul className="mt-2 max-h-56 space-y-1.5 overflow-y-auto pr-1">
        {shown.map((item, index) => (
          <li
            key={`${item.scope ?? ''}-${item.kind}-${item.text}-${index}`}
            className="rounded-lg bg-white/80 px-2 py-1.5"
          >
            <span className="flex min-w-0 flex-wrap items-baseline gap-1.5">
              {/* 是字还是词：一眼看出来（同一个「上」在字表和词语表里是两件事）。 */}
              <span className="shrink-0 rounded bg-slate-100 px-1 text-[11px] font-medium text-slate-500">
                {item.kind === 'char' ? '字' : '词'}
              </span>
              {/* **哪个字/词**：词元原文，等宽、加粗（要能照着去原文里找）。 */}
              <span className="min-w-0 break-all font-mono text-base font-semibold text-slate-900">
                {item.text}
              </span>
              {/*
                **多音字**：标出"有几个读音"。用户原话是"多音字有几个（还要列出来）"，
                所以条数写在这里、读音写在下面一行（都不藏在长句里）。
              */}
              {item.action === 'polyphonic' && (
                <span className="shrink-0 rounded bg-sky-100 px-1 text-[11px] font-semibold text-sky-800">
                  多音字（{item.readings.length > 0 ? `${item.readings.length} 个读音` : '读音没拿到'}）
                </span>
              )}
            </span>

            {/* **多音字的读音**：`zhǎng / cháng`（用户举的例子就是这个形状）。 */}
            {item.action === 'polyphonic' && (
              <span className="mt-0.5 block font-mono text-xs text-sky-900">
                读音：{item.readings.length > 0 ? item.readings.join(' / ') : '（读音没拿到）'}
              </span>
            )}

            {/* **哪几行**：行号单独高亮，同一行写了几次也说出来。 */}
            <span className="mt-1 flex flex-wrap items-center gap-1">
              <span className={cn('text-[11px]', toneHint)}>出现在</span>
              {item.lines.map((entry) => (
                <span
                  key={entry.line}
                  className={cn(
                    'rounded px-1 text-xs font-semibold tabular-nums',
                    toneBadge,
                  )}
                >
                  第 {entry.line} 行
                  {entry.times > 1 && `（${entry.times} 次）`}
                </span>
              ))}
            </span>

            {/* **会怎么处理**：合并成 1 条 / 多音字只存 1 条 / 跳过 / 更新。 */}
            <span className={cn('mt-1 block text-xs', tone === 'warn' ? 'text-amber-900' : tone === 'info' ? 'text-sky-900' : 'text-slate-600')}>
              → {describeDuplicateAction(item)}
            </span>
          </li>
        ))}
      </ul>

      {folded.hidden > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={cn(
            'mt-1.5 rounded-lg border bg-white px-2 py-1 text-xs font-semibold',
            tone === 'warn'
              ? 'border-amber-200 text-amber-700'
              : 'border-slate-300 text-slate-700',
          )}
        >
          展开全部 {folded.total} 条
        </button>
      )}
      {folded.hidden > 0 && expanded && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className={cn(
            'mt-1.5 rounded-lg border bg-white px-2 py-1 text-xs font-semibold',
            tone === 'warn'
              ? 'border-amber-200 text-amber-700'
              : 'border-slate-300 text-slate-700',
          )}
        >
          收起
        </button>
      )}
    </div>
  );
}

/**
 * 预览卡片里的**重复明细块**（两个导入面板共用一份）。
 *
 * `duplicates === null`（后端没给这块）时：
 *   · 后端确实说过有重复 → 明说"明细没拿到"（**不是**"没有重复"）；
 *   · 连重复数都是 0 / 没拿到 → 什么都不渲染（不画空框）。
 */
export function DuplicateReport({
  duplicates,
  mergedCount = null,
  polyphonicCount = null,
}: {
  duplicates: {
    merged: readonly ImportDuplicateItem[];
    /** 多音字重复的（不算重复，字表里只存 1 条，带 `readings`）。 */
    polyphonic: readonly ImportDuplicateItem[];
    existing: readonly ImportDuplicateItem[];
  } | null;
  /** 后端那张账里的"份内重复·打错的"条数（`null` = 没拿到）。 */
  mergedCount?: number | null;
  /** 后端那张账里的"多音字重复"条数（`null` = 没拿到）。 */
  polyphonicCount?: number | null;
}) {
  if (duplicates === null) {
    if ((mergedCount === null || mergedCount <= 0) && (polyphonicCount === null || polyphonicCount <= 0)) {
      return null;
    }
    return (
      <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        这次有{' '}
        {mergedCount !== null && mergedCount > 0 ? `${mergedCount} 条「同一份里重复」` : ''}
        {mergedCount !== null && mergedCount > 0 && polyphonicCount !== null && polyphonicCount > 0
          ? '、'
          : ''}
        {polyphonicCount !== null && polyphonicCount > 0 ? `${polyphonicCount} 条「多音字重复」` : ''}
        ，但<strong>没拿到逐条明细</strong>（后端没返回
        <code className="mx-1 rounded bg-white px-1">duplicates</code>）—— 这不是"没有重复"，
        请刷新重试。
      </p>
    );
  }
  if (
    duplicates.merged.length === 0 &&
    duplicates.polyphonic.length === 0 &&
    duplicates.existing.length === 0
  ) {
    return null;
  }

  return (
    <div className="mt-3 space-y-2">
      {duplicates.merged.length > 0 && (
        <DuplicateDetailList
          title="同一份里重复的（打错了，会合并成 1 条）"
          hint="这个字/词只有一个读音 —— 多半是手误粘了两遍。只会入库 1 条：保留第一次出现的那一行，其余不再单独写入。这只是提示，这一批照样导入。"
          items={duplicates.merged}
          tone="warn"
        />
      )}
      {/*
        ⚠️ 2026-10-21 用户要求删掉「多音字重复的（不是打错，字表里只存 1 条）」整块
           （含它下面那 3 个条目）—— "说明文字块太多了"。
           `duplicates.polyphonic` 数据**照旧从后端来**（蓝底汇总块里的
           「多音字（重复出现的）：N 个」仍用它），只是这里不再单独列一遍。
      */}
      {duplicates.existing.length > 0 && (
        <DuplicateDetailList
          title="之前已经录过的（会跳过 / 会更新）"
          hint="这些字/词库里已经有了：内容一致就跳过（不会重复写入），属性有差异才更新。重复导入同一份表时这里会很长，是正常的。"
          items={duplicates.existing}
          tone="neutral"
        />
      )}
    </div>
  );
}
